import React, { useState } from "react";
import { StyleSheet, View, ScrollView, TouchableOpacity } from "react-native";
import { ContactModel, GroupModel, ShareModel, MemoryModel, formatLocation } from "common247";
import { router } from "expo-router";
import ThemedScreen from "@/components/ThemedScreen";
import { ThemedText } from "@/components/ThemedText";
import { ThemedPanel } from "@/components/ThemedPanel";
import { ThemedToggle } from "@/components/ThemedToggle";
import { SharingControl } from "@/components/sharing/SharingControl";
import { ShareSelector } from "@/components/sharing/ShareSelector";
import { useThemeColor } from "@/hooks/useThemeColor";

/**
 * Mock Memory object used as the subject of sharing.
 */
const MOCK_MEMORY: MemoryModel = {
  id: "mem-design-001",
  ownerId: "current-user",
  title: "Summer Trip to Kyoto",
  date: "2026-08-15",
  description: "Exploring temples, bamboo groves, and traditional tea houses in Kyoto.",
  location: { text: "Kyoto, Japan" },
  mediaIds: [],
  userRole: "owner",
  canShare: true,
  createdAt: Date.parse("2026-08-15T10:00:00.000Z"),
};

/**
 * 5 Test Contacts:
 * - 3 owned by current user
 * - 2 shared from other users
 */
const INITIAL_MOCK_CONTACTS: ContactModel[] = [
  {
    id: "c-1",
    displayName: "Alice Walker",
    email: "alice.w@example.com",
    userRole: "owner",
    ownerId: "current-user",
    createdAt: Date.parse("2026-01-10T12:00:00.000Z"),
    isActive: true,
  },
  {
    id: "c-2",
    displayName: "Charlie Brown",
    email: "charlie.b@example.com",
    userRole: "owner",
    ownerId: "current-user",
    userId: "usr-charlie",
    createdAt: Date.parse("2026-02-14T15:30:00.000Z"),
    isActive: true,
  },
  {
    id: "c-3",
    displayName: "Diana Prince",
    email: "diana.p@example.com",
    userRole: "owner",
    ownerId: "current-user",
    createdAt: Date.parse("2026-03-01T09:15:00.000Z"),
    isActive: true,
  },
  {
    id: "c-4",
    displayName: "Edward Norton",
    email: "edward.n@example.com",
    userRole: "viewer",
    ownerId: "usr-robert",
    ownerDisplayName: "Robert Green",
    createdAt: Date.parse("2026-04-12T18:20:00.000Z"),
    isActive: true,
  },
  {
    id: "c-5",
    displayName: "Fiona Gallagher",
    email: "fiona.g@example.com",
    userRole: "editor",
    ownerId: "usr-sarah",
    ownerDisplayName: "Sarah Connor",
    userId: "usr-fiona",
    createdAt: Date.parse("2026-05-20T11:45:00.000Z"),
    isActive: true,
  },
];

/**
 * 5 Test Groups:
 * - 3 owned by current user
 * - 2 shared from other users
 */
const INITIAL_MOCK_GROUPS: GroupModel[] = [
  {
    id: "g-1",
    name: "Family Memories",
    description: "Family group",
    userRole: "owner",
    ownerId: "current-user",
    contactIds: ["c-1", "c-2"],
    createdAt: Date.parse("2026-01-01T00:00:00.000Z"),
    updatedAt: Date.parse("2026-01-01T00:00:00.000Z"),
  },
  {
    id: "g-2",
    name: "Hiking Club",
    description: "Weekend hikes",
    userRole: "owner",
    ownerId: "current-user",
    contactIds: ["c-2", "c-3"],
    createdAt: Date.parse("2026-02-01T00:00:00.000Z"),
    updatedAt: Date.parse("2026-02-01T00:00:00.000Z"),
  },
  {
    id: "g-3",
    name: "Close Friends",
    description: "Friends group",
    userRole: "owner",
    ownerId: "current-user",
    contactIds: ["c-1", "c-3"],
    createdAt: Date.parse("2026-03-01T00:00:00.000Z"),
    updatedAt: Date.parse("2026-03-01T00:00:00.000Z"),
  },
  {
    id: "g-4",
    name: "Project Collaborators",
    description: "Work projects",
    userRole: "viewer",
    ownerDisplayName: "Alex Turner",
    ownerId: "usr-alex",
    contactIds: ["c-4"],
    createdAt: Date.parse("2026-04-01T00:00:00.000Z"),
    updatedAt: Date.parse("2026-04-01T00:00:00.000Z"),
  },
  {
    id: "g-5",
    name: "Photography Workshop",
    description: "Photography club",
    userRole: "editor",
    ownerDisplayName: "Elena Rostova",
    ownerId: "usr-elena",
    contactIds: ["c-5"],
    createdAt: Date.parse("2026-05-01T00:00:00.000Z"),
    updatedAt: Date.parse("2026-05-01T00:00:00.000Z"),
  },
];

/**
 * Initial Shares pre-populating "Shared With":
 * - 1 contact share (Alice Walker)
 * - 1 group share (Project Collaborators - shared by Alex Turner)
 */
const INITIAL_MOCK_SHARES: ShareModel[] = [
  {
    id: "share-mock-1",
    sourceId: MOCK_MEMORY.id!,
    sourceType: "memory",
    targetId: "c-1",
    targetType: "contact",
    targetDisplayName: "Alice Walker",
    role: "viewer",
    sharedBy: "current-user",
    sharedAt: Date.parse("2026-08-20T14:00:00.000Z"),
  },
  {
    id: "share-mock-2",
    sourceId: MOCK_MEMORY.id!,
    sourceType: "memory",
    targetId: "g-4",
    targetType: "group",
    targetDisplayName: "Project Collaborators",
    role: "editor",
    sharedBy: "current-user",
    sharedAt: Date.parse("2026-08-22T09:30:00.000Z"),
  },
];

/**
 * Design testing screen for the SharingControl component.
 * Allows interactive evaluation of contacts, groups, owner context, roles, and revoke actions.
 */
export default function SharingControlDesignScreen() {
  const [shares, setShares] = useState<ShareModel[]>(INITIAL_MOCK_SHARES);
  const [contacts] = useState<ContactModel[]>(INITIAL_MOCK_CONTACTS);
  const [groups] = useState<GroupModel[]>(INITIAL_MOCK_GROUPS);

  // Workbench interactive toggles
  const [isLoading, setIsLoading] = useState(false);
  const [canShare, setCanShare] = useState(true);
  const [canUnshare, setCanUnshare] = useState(true);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isPopupVisible, setIsPopupVisible] = useState(false);

  const badgeBg = useThemeColor({}, "buttonSecondary");
  const tintColor = useThemeColor({}, "tint");

  /**
   * Handles adding a new share or updating an existing share role in local test state.
   */
  const handleShare = (targetId: string, targetType: "contact" | "group", role: "viewer" | "editor") => {
    const existingIndex = shares.findIndex((s) => s.targetId === targetId && s.targetType === targetType);
    if (existingIndex >= 0) {
      setShares((prev) => prev.map((s, idx) => (idx === existingIndex ? { ...s, role } : s)));
      return;
    }

    let displayName = targetId;
    let targetUserId: string | undefined;

    if (targetType === "contact") {
      const contact = contacts.find((c) => c.id === targetId);
      if (contact) {
        displayName = contact.displayName || contact.email || targetId;
        targetUserId = contact.userId;
      }
    } else {
      const group = groups.find((g) => g.id === targetId);
      if (group) {
        displayName = group.name || targetId;
      }
    }

    const newShare: ShareModel = {
      id: `share-mock-${Date.now()}`,
      sourceId: MOCK_MEMORY.id!,
      sourceType: "memory",
      targetId,
      targetType,
      targetDisplayName: displayName,
      targetUserId,
      role,
      sharedBy: "current-user",
      sharedAt: Date.now(),
    };

    setShares((prev) => [...prev, newShare]);
  };

  /**
   * Handles revoking an existing share from local test state.
   */
  const handleUnshare = (shareId: string) => {
    setShares((prev) => prev.filter((s) => s.id !== shareId));
  };

  /**
   * Resets mock data back to initial test state.
   */
  const handleReset = () => {
    setShares(INITIAL_MOCK_SHARES);
    setIsLoading(false);
    setCanShare(true);
    setCanUnshare(true);
    setIsReadOnly(false);
  };

  return (
    <ThemedScreen
      title="Design: Sharing Control"
      leftIcon={{
        name: "arrow-back",
        onPress: () => router.navigate("/admin/design" as any),
      }}
    >
      <ScrollView contentContainerStyle={styles.container}>
        {/* Memory Context Banner */}
        <ThemedPanel padding={16} elevation="low" style={{ marginBottom: 16 }}>
          <View style={styles.memoryHeader}>
            <ThemedText style={styles.memoryBadge}>MEMORY CONTEXT</ThemedText>
            <ThemedText style={styles.memoryDate}>📍 {formatLocation(MOCK_MEMORY.location)}</ThemedText>
          </View>
          <ThemedText type="defaultSemiBold" style={styles.memoryTitle}>
            {MOCK_MEMORY.title}
          </ThemedText>
          <ThemedText style={styles.memoryDesc}>{MOCK_MEMORY.description}</ThemedText>
        </ThemedPanel>

        {/* Workbench Control Panel */}
        <ThemedPanel padding={14} elevation="low" style={{ marginBottom: 16 }}>
          <ThemedText type="defaultSemiBold" style={styles.panelTitle}>
            Sandbox Controls
          </ThemedText>
          <View style={styles.toggleRow}>
            <ThemedToggle
              label="Loading"
              value={isLoading}
              onValueChange={setIsLoading}
              style={styles.toggleItem}
            />
            <ThemedToggle
              label="Can Share"
              value={canShare}
              onValueChange={setCanShare}
              style={styles.toggleItem}
            />
            <ThemedToggle
              label="Can Revoke"
              value={canUnshare}
              onValueChange={setCanUnshare}
              style={styles.toggleItem}
            />
            <ThemedToggle
              label="Read-Only Mode"
              value={isReadOnly}
              onValueChange={setIsReadOnly}
              style={styles.toggleItem}
            />
          </View>

          <TouchableOpacity style={[styles.resetBtn, { backgroundColor: badgeBg }]} onPress={handleReset} activeOpacity={0.7}>
            <ThemedText style={[styles.resetBtnText, { color: tintColor }]}>🔄 Reset Test Data</ThemedText>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.resetBtn, { backgroundColor: tintColor, marginTop: 10 }]}
            onPress={() => setIsPopupVisible(true)}
            activeOpacity={0.7}
          >
            <ThemedText style={[styles.resetBtnText, { color: "#FFFFFF" }]}>
              ✨ Test ShareSelector (Modal Dialog)
            </ThemedText>
          </TouchableOpacity>
        </ThemedPanel>

        {/* The Live Component Under Test */}
        <SharingControl
          shares={shares}
          contacts={contacts}
          groups={groups}
          isLoading={isLoading}
          canShare={canShare}
          canUnshare={canUnshare}
          readOnly={isReadOnly}
          onShare={handleShare}
          onUnshare={handleUnshare}
        />

        {/* Contextual Archival Share Selector */}
        <ShareSelector
          visible={isPopupVisible}
          onClose={() => setIsPopupVisible(false)}
          itemTitle={MOCK_MEMORY.title}
          itemType="memory"
          shares={shares}
          contacts={contacts}
          groups={groups}
          isLoading={isLoading}
          canShare={canShare}
          canUnshare={canUnshare}
          readOnly={isReadOnly}
          onShare={handleShare}
          onUnshare={handleUnshare}
          testID="design-showcase-sharing-popup"
        />
      </ScrollView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
    paddingBottom: 40,
  },
  memoryHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  memoryBadge: {
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 1,
    opacity: 0.5,
  },
  memoryDate: {
    fontSize: 12,
    opacity: 0.6,
  },
  memoryTitle: {
    fontSize: 18,
    marginBottom: 4,
  },
  memoryDesc: {
    fontSize: 13,
    opacity: 0.7,
    lineHeight: 18,
  },
  panelTitle: {
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    opacity: 0.6,
    marginBottom: 10,
  },
  toggleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  toggleItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  resetBtn: {
    paddingVertical: 8,
    borderRadius: 0,
    alignItems: "center",
  },
  resetBtnText: {
    fontSize: 12,
    fontWeight: "700",
  },
});
